import React, { useCallback, useEffect, useState, useMemo } from 'react'
import { StyleSheet, TouchableWithoutFeedback, ViewStyle } from 'react-native'
import format from 'date-fns/format'
import {
  DNAButton,
  DNAButtonProps,
  DNANotifications,
  DNAIcon,
  DNABox,
  Iffy,
} from '@alucio/lux-ui'
import { DNANotificationsItemProps } from '@alucio/lux-ui/src/components/layout/DNANotifications/DNANotifications'
import { DNAPopover } from 'src/components/DNA'
import { useHistory } from 'src/router'
import { useAppSettings, DeviceMode } from 'src/state/context/AppSettings'
import folderQuery from 'src/state/redux/folder/query'
import {
  useAllFoldersUpdatedDocsMap,
  useIsSharedFoldersHydrated,
  useIsSharedFoldersStatus,
  useSharedFolders,
} from 'src/state/redux/selector/folder'
import { SliceStatus } from 'src/state/redux/slice/common'
import { useFetchSharedFolders } from 'src/state/redux/slice/sharedFolder'
import { ROUTES } from 'src/router/routes'
import useFeatureFlags from 'src/hooks/useFeatureFlags/useFeatureFlags'
import { getSharedFolderUpdateNotifications } from 'src/utils/sharedFolderUpdates'
import { useBulletins } from 'src/screens/Bulletin/useBulletins'

const styles = StyleSheet.create({
  notificationRedDot: {
    width: 16,
    height: 16,
  },
})

interface Styles {
  wrapper: ViewStyle,
}

type DeviceModeStyles = Record<DeviceMode, StyleSheet.NamedStyles<Styles>>

const deviceModeStylesUI2: DeviceModeStyles = {
  desktop: StyleSheet.create({
    wrapper: {
      position: 'absolute',
      right: 1,
      top: -1,
      cursor: 'pointer',
    },
  }),
  tablet: StyleSheet.create({
    wrapper: {
      position: 'absolute',
      right: 7,
      top: 4,
      cursor: 'pointer',
    },
  }),
}

type DeviceModeButtonProps = Record<DeviceMode, DNAButtonProps>
const deviceModeButtonProps: DeviceModeButtonProps = {
  desktop: {
    appearance: 'outline',
    status: 'tertiary',
    size: 'lg',
  },
  tablet: {
    appearance: 'ghost',
    status: 'subtle',
    rounded: true,
    size: 'xl',
    colorMode: 'dark',
  },
}

export const Notification = () => {
  const { deviceMode, isOnline } = useAppSettings();
  const history = useHistory();
  const [contentVisible, setContentVisible] = useState<boolean>(false);
  const currentActiveFilter = useMemo(() => folderQuery.merge(folderQuery.filters.active), []);
  const updatedDocumentsMap = useAllFoldersUpdatedDocsMap(currentActiveFilter);
  const {
    enableNotificationUpdates, enableSharedFolders, BEAC_8524_beacon_bulletin: bulletinEnabled,
  } = useFeatureFlags(
    'enableNotificationUpdates',
    'enableSharedFolders',
    'BEAC_8524_beacon_bulletin',
  );
  const { bulletins, canRead } = useBulletins();
  const latestBulletin = bulletins[0];
  const sharedFolders = useSharedFolders();
  const isSharedFoldersHydrated = useIsSharedFoldersHydrated();
  const isSharedFoldersNew = useIsSharedFoldersStatus(SliceStatus.NEW);
  const { fetchSharedFolders } = useFetchSharedFolders();

  const updatedDocumentsArray = Array.from(updatedDocumentsMap.entries())
  const sharedFolderUpdateNotifications = useMemo(() => {
    if (!enableSharedFolders) return [];

    return getSharedFolderUpdateNotifications(sharedFolders);
  }, [contentVisible, enableSharedFolders, sharedFolders])
  const hasDocumentUpdates = updatedDocumentsArray.length > 0 && enableNotificationUpdates
  const hasSharedFolderUpdates = sharedFolderUpdateNotifications.length > 0
  const hasUpdates = hasDocumentUpdates || hasSharedFolderUpdates
  const toggleContent = useCallback(() => setContentVisible(p => !p), [])
  const closeContent = useCallback(() => setContentVisible(false), [])
  const currentDeviceModeButtonProps = deviceModeButtonProps[deviceMode]
  const currentDeviceModeStyle = deviceModeStylesUI2[deviceMode]

  useEffect(() => {
    if (enableSharedFolders && isOnline && !isSharedFoldersHydrated && isSharedFoldersNew) {
      fetchSharedFolders();
    }
  }, [enableSharedFolders, fetchSharedFolders, isOnline, isSharedFoldersHydrated, isSharedFoldersNew])

  const items: DNANotificationsItemProps[] = []
  if (hasSharedFolderUpdates) {
    sharedFolderUpdateNotifications.forEach(notification => {
      items.push({
        id: notification.id,
        date: format(new Date(notification.latestUpdatedAt), 'MMMM dd, yyyy'),
        shortDescription: 'New updates in shared folders',
        icon: 'folder-simple-fill',
        details: notification.folders.map(folder => ({
          id: folder.model.id,
          label: folder.model.name,
          onPress: () => {
            history.push(`${ROUTES.SHARED_FOLDERS.PATH}/${folder.model.id}`)
            closeContent();
          },
        })),
      })
    })
  }

  if (hasDocumentUpdates) {
    let maxDate: Date | undefined
    updatedDocumentsArray.forEach(obj => {
      const doc = obj[0]
      if (doc) {
        const date = new Date(doc.model.updatedAt)
        if (!maxDate || date > maxDate) {
          maxDate = date
        }
      }
    })

    items.push({
      date: format(maxDate || new Date(), 'MMMM dd, yyyy'),
      shortDescription: 'New version updates',
      actionLabel: 'Manage updates',
      icon: 'file-fill',
      onPress: () => {
        history.push('/folders/updates')
        closeContent();
      },
    })
  }

  if (bulletinEnabled && canRead) {
    items.push({
      id: 'bulletin',
      date: latestBulletin ? format(new Date(latestBulletin.publishedAt), 'MMM d, yyyy') : '',
      shortDescription: latestBulletin?.title || 'Bulletin',
      icon: 'newspaper',
      onPress: () => {
        history.push('/bulletin');
        closeContent();
      },
    });
  }

  // Current default notification
  items.push({
    date: 'Oct 28th, 2026',
    shortDescription: 'New features available!',
    actionLabel: 'See what’s new',
    icon: 'star',
    highlight: true,
    onPress: useCallback(() => {
      history.push('/notifications')
      closeContent();
    }, [history, closeContent]),
  })

  return (
    <DNAPopover
      lazyMount
      placement="bottom-end"
      interactive={true}
      visible={contentVisible}
      onBackdropPress={toggleContent}
      type="menu"

    >
      <DNAPopover.Anchor style={{ height: '100%' }}>
        <DNAButton
          {...currentDeviceModeButtonProps}
          testID="notification-bell"
          iconLeft="bell"
          onPress={toggleContent}
          appearance={'ghost'}
          status={'tertiary'}
          size={deviceMode === 'desktop' ? 'md' : 'xl'}
          rounded={false}
          colorMode={'light'}

        />
        <Iffy is={hasUpdates}>
          <TouchableWithoutFeedback onPress={toggleContent}>
            <DNABox style={currentDeviceModeStyle.wrapper}>
              <DNAIcon.Styled name="circle-fill" status="danger" style={styles.notificationRedDot} />
            </DNABox>
          </TouchableWithoutFeedback>
        </Iffy>
      </DNAPopover.Anchor>

      <DNAPopover.Content offset={10}>
        <DNANotifications
          items={items}
          onPress={useCallback(() => {
            history.push('/notifications')
            closeContent();
          }, [history, closeContent])}
        />
      </DNAPopover.Content>
    </DNAPopover>
  )
}

export default Notification
